/**
 * The demo's lead submission (SKE-4166, phase 3) — client half.
 *
 * WHY THIS DOESN'T GO THROUGH `api/` / `utils/axios`
 * -------------------------------------------------
 * Every other call this SPA makes goes to the sked-api gateway
 * (`VITE_API_URL` -> api.skedsocial.com). The lead endpoint lives on
 * schedugram-server instead, because that is where the three services it needs
 * already exist — HubSpot (`services/hubspot.ts`), server-side GA4/sGTM
 * (`services/serverGtm.ts`) and Turnstile (`services/cloudflareTurnstile.ts`).
 * Hosting it on sked-api would have meant duplicating all three. So the lead
 * POST is a plain `fetch` at its own base URL (`VITE_DEMO_LEAD_API_URL`) and
 * deliberately does NOT reuse the shared axios instance, whose baseURL and
 * interceptors belong to the review API.
 *
 * NO CREDENTIALS: the request is anonymous and `credentials` is left at the
 * default `same-origin`, so no cookie is ever sent cross-origin. The endpoint
 * is CSRF-exempt by virtue of its `/public` prefix and is protected by
 * Turnstile + a per-IP rate limit instead.
 */

import type { DemoAttribution } from './demoAttribution'
import { getDemoAttribution, getDemoSessionId } from './demoAttribution'

/** Path on schedugram-server. Kept in one place; the server mirrors it. */
export const DEMO_LEAD_PATH = '/public/demo-lead'

/**
 * Where the demo's marketing surface lives, sent so the server can use a real
 * `page_location` on the GA4 conversion instead of the app's own site URL.
 * Falls back to the current page when it can't be read.
 */
const currentPageUrl = (): string => {
  const location = globalThis.window?.location
  if (!location) {
    return ''
  }
  // Strip the SPA's hash route: `#/review/demo/posts/{id}` is per-visitor noise
  // and would fragment the campaign's page_location in GA4.
  return `${location.origin}${location.pathname}${location.search}`
}

/**
 * The endpoint base. Unset means the lead pipeline is not configured for this
 * build — the gate is not rendered at all in that case (see
 * `isDemoLeadCaptureConfigured`) rather than showing a form that can only fail,
 * which would also corrupt the email-unlock KPI with impossible denominators.
 */
export const getDemoLeadEndpoint = (): string | null => {
  const base = import.meta.env.VITE_DEMO_LEAD_API_URL?.trim()

  if (!base) {
    warnOnceAboutMissingEndpoint()
    return null
  }

  return `${base.replace(/\/+$/, '')}${DEMO_LEAD_PATH}`
}

export const isDemoLeadCaptureConfigured = (): boolean =>
  Boolean(import.meta.env.VITE_DEMO_LEAD_API_URL?.trim())

let hasWarnedAboutMissingEndpoint = false
const warnOnceAboutMissingEndpoint = () => {
  if (hasWarnedAboutMissingEndpoint) {
    return
  }
  hasWarnedAboutMissingEndpoint = true
  console.warn(
    '[external-review] VITE_DEMO_LEAD_API_URL is not set — the approvals demo ' +
      'will not offer its email unlock.'
  )
}

/**
 * Deliberately permissive: this is a UX affordance so an obvious typo doesn't
 * cost a round-trip, NOT validation. HubSpot is the authority on whether an
 * address is real, and the server re-checks the same shape.
 */
export const isLikelyEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim())

export interface DemoLeadPayload {
  email: string
  /** Cloudflare Turnstile token; omitted when no site key is configured. */
  turnstileToken?: string
  /** Ties this lead to the browser events fired for the same visit. */
  sessionId: string
  /** How many approve/reject/note actions the visitor took before unlocking. */
  actionCount: number
  /** Real marketing URL the visitor is on, for the GA4 `page_location`. */
  pageUrl: string
  vertical?: string
  attribution: DemoAttribution
}

export interface BuildDemoLeadPayloadInput {
  email: string
  turnstileToken?: string
  actionCount?: number
  /** Overrides for tests; production reads the live browser context. */
  sessionId?: string
  pageUrl?: string
  attribution?: DemoAttribution
}

/**
 * Pure payload builder — the shape the server validates against, assembled in
 * one place so it can be tested without a DOM or a network.
 */
export const buildDemoLeadPayload = (
  input: BuildDemoLeadPayloadInput
): DemoLeadPayload => {
  const attribution = input.attribution ?? getDemoAttribution()

  return {
    email: input.email.trim().toLowerCase(),
    ...(input.turnstileToken ? { turnstileToken: input.turnstileToken } : {}),
    sessionId: input.sessionId ?? getDemoSessionId(),
    actionCount: input.actionCount ?? 0,
    pageUrl: input.pageUrl ?? currentPageUrl(),
    ...(attribution.vertical ? { vertical: attribution.vertical } : {}),
    attribution,
  }
}

export type DemoLeadFailureReason =
  | 'not-configured'
  | 'invalid-email'
  | 'captcha'
  | 'rate-limited'
  | 'network'
  | 'server'

export type DemoLeadResult =
  { ok: true } | { ok: false; reason: DemoLeadFailureReason; status?: number }

/** Map the server's status code onto something the UI can speak about. */
const failureForStatus = (status: number): DemoLeadFailureReason => {
  if (status === 429) return 'rate-limited'
  if (status === 400 || status === 422) return 'invalid-email'
  if (status === 403) return 'captcha'
  return 'server'
}

export const submitDemoLead = async (
  payload: DemoLeadPayload
): Promise<DemoLeadResult> => {
  const endpoint = getDemoLeadEndpoint()

  if (!endpoint) {
    return { ok: false, reason: 'not-configured' }
  }

  let response: Response
  try {
    response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
  } catch {
    return { ok: false, reason: 'network' }
  }

  if (!response.ok) {
    // The server distinguishes a failed captcha from a malformed email in its
    // body; fall back to the status code when the body isn't readable.
    let reason = failureForStatus(response.status)
    try {
      const body = (await response.json()) as { code?: string }
      if (body?.code === 'captcha-failed') {
        reason = 'captcha'
      } else if (body?.code === 'invalid-email') {
        reason = 'invalid-email'
      }
    } catch {
      // Non-JSON body — the status-derived reason stands.
    }
    return { ok: false, reason, status: response.status }
  }

  return { ok: true }
}
